import { useCallback, useEffect, useState } from 'react';
import { labCycleAPI, labFleetAPI } from '../services/api';

function errorMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

export default function useLabFleet({ extendExpiry = false } = {}) {
  const [fleet, setFleet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [actionKey, setActionKey] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await labFleetAPI.get();
      setFleet(res.data);
      setError('');
    } catch (err) {
      setError(errorMessage(err, 'Could not load server accounts'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const scan = useCallback(async () => {
    setScanning(true);
    setError('');
    try {
      await labFleetAPI.scan();
      const started = Date.now();
      while (Date.now() - started < 8 * 60 * 1000) {
        await new Promise((resolve) => window.setTimeout(resolve, 2000));
        const progress = await labFleetAPI.progress();
        const status = progress.data?.status;
        if (status === 'finished' || status === 'failed' || status === 'idle') {
          if (status === 'failed') {
            setError(progress.data?.error?.message || 'Server fetch failed');
          }
          break;
        }
      }
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not fetch the servers'));
    } finally {
      setScanning(false);
    }
  }, [load]);

  const act = useCallback(
    async (account, action) => {
      const confirmed = window.confirm(
        action === 'delete'
          ? `Remove the local Linux account ${account.unixUsername} on this server? The home directory stays. A central login with the same name is not deleted. The SMS record stays.`
          : action === 'enable'
            ? extendExpiry
              ? `Enable ${account.unixUsername} on ${account.alias || 'this server'}? This unlocks the login, starts VNC, sets the account expiry 45 days from today, and restarts the password age. The password itself is not changed.`
              : `Enable ${account.unixUsername} on ${account.alias || 'this server'}? This unlocks the login and starts VNC. Account expiry and an expired password are not reset.`
            : `Disable ${account.unixUsername}? This locks the login and stops VNC. The home stays.`
      );
      if (!confirmed) return;
      setActionKey(`${account.unixUsername}:${action}`);
      setError('');
      setNotice('');
      try {
        if (action === 'enable') {
          const res = await labFleetAPI.enable({
            unixUsername: account.unixUsername,
            alias: account.alias,
            home: account.home,
          });
          setNotice(
            `${res.data?.message || `Enabled ${account.unixUsername}.`} Fetch from servers to refresh Account expires.`
          );
        } else {
          const res = await labCycleAPI.act(account.unixUsername, action);
          if (action === 'delete' && res.data?.message) setNotice(res.data.message);
        }
        await load();
      } catch (err) {
        setError(errorMessage(err, `${action} failed for ${account.unixUsername}`));
      } finally {
        setActionKey('');
      }
    },
    [load, extendExpiry]
  );

  const download = useCallback(async ({ recommendation = 'all', q = '' } = {}) => {
    setDownloading(true);
    setError('');
    try {
      const res = await labFleetAPI.exportExcel({
        recommendation: recommendation === 'all' ? undefined : recommendation,
        q: q || undefined,
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `server-accounts-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let message = errorMessage(err, 'Could not download the Excel file');
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          message = parsed.message || message;
        } catch {
          /* keep the fallback */
        }
      }
      setError(message);
    } finally {
      setDownloading(false);
    }
  }, []);

  return {
    fleet,
    loading,
    scanning,
    downloading,
    actionKey,
    error,
    notice,
    reportNotice: setNotice,
    load,
    scan,
    act,
    download,
  };
}
